import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import isEqual from 'lodash-es/isEqual';
import { useDispatch, useSelector } from 'react-redux';

import { callGet } from '@/api/fetcher';
import { setFeatureFlags } from '@/redux/featureFlag/actions';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Features } from '@/shared/types/account';

const endpoint = `/www/api/v1/account/features`;

type APIFeatures = { features: Features };

const listFeatures = async (): Promise<APIFeatures> => callGet(endpoint);

/**
 * Data loader hook — fetches feature flags from the API and syncs them to Redux.
 * Must be mounted once at the root level (Account.tsx).
 * To read flags, use `useSelector(selectFeatureFlags)` instead.
 */
const useFeatureFlag = () => {
  const accountSlug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();
  const selectedFeatures = useSelector(selectFeatureFlags);

  const { data } = useQuery({
    queryKey: [endpoint, accountSlug],
    queryFn: listFeatures,
    enabled: !!accountSlug,
  });

  useEffect(() => {
    // Avoids dispatching the action in every render
    if (data && !isEqual(data.features, selectedFeatures)) {
      dispatch(setFeatureFlags(data.features));
    }
  }, [dispatch, data, selectedFeatures]);
};

export default useFeatureFlag;
